/* eslint-disable react-hooks/rules-of-hooks */
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import type {
  GridColDef,
  GridRowId,
  GridRowModesModel,
  GridRowParams,
} from '@mui/x-data-grid';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid';
import React from 'react';
import { toast } from 'react-toastify';
import { useGetDefaultLedger } from '../../../lib/query/defaultLedger';
import { deleteEntity } from '../../../lib/services/api-service';
import { TableName } from '../../../constants/table';

const commonColDef: Partial<GridColDef> = {
  flex: 1,
  headerClassName: 'gridHeader--header',
};

export const columns = (
  rowModesModel: GridRowModesModel,
  setRowModesModel: React.Dispatch<React.SetStateAction<GridRowModesModel>>,
  tenantId: string,
): GridColDef[] => {
  const { refetch } = useGetDefaultLedger(tenantId);
  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleDelete =
    (PartitionKey: string, rowKey: string, LedgerOCR: string) => async () => {
      await deleteEntity(TableName.DefaultLedger, PartitionKey, rowKey).then(
        () => {
          toast.success(`Deleted Ledger with name: ${LedgerOCR}`);
          refetch();
        },
      );
    };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  return [
    {
      field: 'companyName',
      headerName: 'CompanyName',
      editable: false,
      ...commonColDef,
    },
    {
      field: 'vendorName',
      headerName: 'VendorName',
      editable: false,
      ...commonColDef,
    },
    {
      field: 'LedgerOCR',
      headerName: 'LedgerOCR',
      editable: true,
      ...commonColDef,
    },
    {
      field: 'Rate',
      headerName: 'Rate',
      editable: true,
      sortable: true,
      ...commonColDef,
    },
    {
      field: 'LedgerName',
      headerName: 'LedgerName',
      editable: true,
      sortable: true,
      ...commonColDef,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 1,
      cellClassName: 'actions',
      headerClassName: 'gridHeader--header',
      getActions: (params: GridRowParams) => {
        const { id, row } = params;
        const { PartitionKey } = row;
        const { RowKey } = row;
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              key="save"
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              key="cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            key="edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            key="delete"
            label="Delete"
            onClick={handleDelete(PartitionKey, RowKey, row.LedgerOCR)}
            color="inherit"
          />,
        ];
      },
    },
  ];
};
